/**
 * @description A search input component that debounces setting the search value and provides a clear button
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import IconButton from '@mui/material/IconButton';
import Close from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';

import debounce from '@seccon/shared/utils/debounce';

/**
 * A debounced onChange handler that takes the new search value and passes it to the React setSearch callback
 * @param evt {React.FormEvent<HTMLInputElement>} - react change event containing the new value
 * @param setValue {Function} - React setSearch callback
 */
const handleSearchChange = debounce(
  (newValue, setValue) => {
    setValue(newValue);
  }
);

/**
 * @prop search {string} - the search criteria state
 * @prop setSearch {Function} - sets the search criteria state
 */
const SearchInput = React.forwardRef(
  ({ search, setSearch, ...otherProps }, ref) => {
    // Reference to the html input element
    const inputRef = React.useRef(null);

    /**
     * Handles clear search events by dispatching an event to clear the search
     */
    function handleClearSearch () {
      /**
       * Since we're using TextField as an uncontrolled component, we can't set react state to an empty string to clear the value. Furthermore,
       * we can't set the text field reference to an empty string either because React overrides the value attribute's native setter function.
       *
       * However, we can call the value setter on the window.HTMLInputElement.prototype and use the text field reference as its context. Plus,
       * after we've updated the value we can dispatch an event to trigger the appropriate onChange handler.
       *
       * I realize this could be viewed as breaking the convention of uncontrolled components, but the alternative seemed worse to me.  The other
       * solution I came up with was using TextField as a controlled component and maintaining a secondary internal search value state that would
       * update right away as the user types in input.  But anytime I have to sync multiple sources of truth seems like a big red flag to me.
       *
       * Reference: https://stackoverflow.com/a/46012210
       */

      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
      nativeInputValueSetter.call(inputRef.current, '');

      const inputEvent = new CustomEvent('input', { bubbles: true, detail: { skipDebounce: true } });
      inputRef.current.dispatchEvent(inputEvent);
    }

    /**
     * Handles search value change events by updating the search state
     * @param event {React.FormEvent<HTMLInputElement>} - change event object
     */
    function handleSearchValueChange (evt) {
      const newValue = evt.target.value.trim().toLowerCase();
      const skipDebounce = evt.nativeEvent.detail?.skipDebounce ?? false;

      // Clear search events should skip the debounce and update the search state right away
      if (skipDebounce) {
        setSearch(newValue);
      } else {
        handleSearchChange(newValue, setSearch);
      }
    }

    // Hide the search clear button if there is no search applied
    const searchClearBtn = search ? (
      <InputAdornment position='end'>
        <IconButton onClick={() => handleClearSearch()}>
          <Close fontSize='small' />
        </IconButton>
      </InputAdornment>
    ) : null;

    // Retrieve any input properties passed to the component
    const inputProps = otherProps?.InputProps ?? {};

    return (
      <TextField
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...otherProps}
        ref={ref}
        InputProps={{ ...inputProps, endAdornment: searchClearBtn, inputRef }}
        onChange={(evt) => handleSearchValueChange(evt)}
      />
    );
  }
);

SearchInput.propTypes = {
  search: PropTypes.string.isRequired,
  setSearch: PropTypes.func.isRequired
}

export default SearchInput;
